import styled from 'astroturf/react';
import React, { ReactNode } from 'react';

import { FollowableUser } from 'js/social/shared/types';
import { Body } from 'js/design_system/Body';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { AuthorHeader } from './AuthorHeader';
import { ImpressionPropsGenerator } from 'js/social/ImpressionHelper';

const Wrapper = styled.div<{ variant?: 'default' | 'dense' }>`
  padding: 16px 16px 12px;
  &.variant-dense {
    padding: 0 0 12px 0;
  }
`;

const SmallAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const AvatarLink = styled.a`
  display: block;
  line-height: 0;
  flex-shrink: 0;
`;

const ReasonWrapper = styled.div`
  padding-bottom: 12px;
  border-bottom: 1px solid var(--stroke-light, #dddde2);
  margin-bottom: 12px;
`;

const ReasonText = styled(Body)`
  @import 'stylesheets/design_system/_util';
  @include text-ellipsis(1);
`;

const ReasonLink = styled(BodyLink)`
  @import 'stylesheets/design_system/_util';
  @include text-ellipsis(1);
`;

const RelatedUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
`;

const PostName = styled(Body)`
  @import 'stylesheets/design_system/_util';
  @include text-ellipsis(1);
  font-weight: 600;
`;

const AutoGroupReason = ({
  reason,
  autoGroup,
}: {
  reason: Reason;
  autoGroup: string;
}) => {
  if (reason === 'new_paper_in_auto_group') {
    return (
      <ReasonWrapper>
        <ReasonText size="sm">New paper in {autoGroup}</ReasonText>
      </ReasonWrapper>
    );
  } else if (reason === 'auto_group_comment') {
    return (
      <ReasonWrapper>
        <ReasonText size="sm">New comment in {autoGroup}</ReasonText>
      </ReasonWrapper>
    );
  }
  return null;
};

const RelatedUserReason = ({
  reason,
  relatedUser,
}: {
  reason: Reason;
  relatedUser: FollowableUser;
}) => {
  if (reason === 'user_liked') {
    return (
      <ReasonWrapper>
        <RelatedUserInfo>
          <AvatarLink href={relatedUser.url}>
            <SmallAvatar src={relatedUser.photo} />
          </AvatarLink>
          {/* spacer */}
          <div />
          <ReasonLink size="sm" bold href={relatedUser.url}>
            {relatedUser.displayName}
          </ReasonLink>
          <Body size="sm">liked</Body>
        </RelatedUserInfo>
      </ReasonWrapper>
    );
  } else if (reason === 'user_commented') {
    return (
      <ReasonWrapper>
        <RelatedUserInfo>
          <AvatarLink href={relatedUser.url}>
            <SmallAvatar src={relatedUser.photo} />
          </AvatarLink>
          {/* spacer */}
          <div />
          <ReasonLink size="sm" bold href={relatedUser.url}>
            {relatedUser.displayName}
          </ReasonLink>
          <Body size="sm">commented</Body>
        </RelatedUserInfo>
      </ReasonWrapper>
    );
  }
  return null;
};

type Reason =
  | 'new_paper_in_auto_group'
  | 'user_liked'
  | 'user_commented'
  | 'auto_group_comment';

type AutoGroupV1Props = {
  type: 'auto_group_recommended' | 'user_post';
  reason: Reason | null;
  authorUser: FollowableUser | null;
  relatedUser: FollowableUser | null;
  autoGroup: string | null;
};

type AutoGroupV2Props = {
  type: 'autogroup_multi_user_recommended';
  relevantUsers: FollowableUser[];
  autoGroupTitle?: string;
  action: 'save' | 'view';
  tooltipContents: ReactNode;
};

type RecommendationProps = {
  type: 'recommended';
};

type PostProps = {
  type: 'post';
  author: FollowableUser;
};

type SharedProps = {
  impressionPropsGen: ImpressionPropsGenerator;
  timestamp: string;
  variant?: 'default' | 'dense';
};

type Props = SharedProps &
  (AutoGroupV1Props | AutoGroupV2Props | RecommendationProps | PostProps);

export const NewsHeader = (props: Props) => {
  const { type, timestamp, impressionPropsGen, variant = 'default' } = props;

  if (type === 'post') {
    const { author } = props;
    return (
      <Wrapper variant={variant}>
        <AuthorHeader
          kind="user"
          user={{ ...author, displayName: author.firstName }}
          size="md"
          showAvatar
          showTimestamp
          impressionPropsGen={impressionPropsGen}
          variant={variant}
        />
      </Wrapper>
    );
  } else if (type === 'user_post' || type === 'auto_group_recommended') {
    const { reason, authorUser, relatedUser, autoGroup } = props;

    const headerReason = (
      <>
        {reason === 'new_paper_in_auto_group' && autoGroup && (
          <AutoGroupReason reason={reason} autoGroup={autoGroup} />
        )}
        {reason === 'user_liked' && relatedUser && (
          <RelatedUserReason reason={reason} relatedUser={relatedUser} />
        )}
        {reason === 'user_commented' && relatedUser && (
          <RelatedUserReason reason={reason} relatedUser={relatedUser} />
        )}
        {reason === 'auto_group_comment' && autoGroup && (
          <AutoGroupReason reason={reason} autoGroup={autoGroup} />
        )}
      </>
    );

    if (type === 'user_post' && authorUser) {
      return (
        <Wrapper variant={variant}>
          {headerReason}
          <AuthorHeader
            kind="user"
            user={authorUser}
            size="md"
            showAvatar
            showTimestamp
            timestamp={timestamp}
            impressionPropsGen={impressionPropsGen}
            variant={variant}
          />
        </Wrapper>
      );
    } else if (type === 'auto_group_recommended' && autoGroup) {
      return variant === 'default' ? (
        <Wrapper variant={variant}>
          {headerReason}
          <AuthorHeader
            kind="recommendation"
            recommendation={{
              name: `Topic: ${autoGroup}`,
              subtitle: 'Recommended based on your interests',
            }}
            size="md"
            showAvatar
            impressionPropsGen={impressionPropsGen}
            variant={variant}
          />
        </Wrapper>
      ) : (
        <Wrapper variant={variant}>
          {headerReason}
          <PostName size="md">Recommended paper</PostName>
        </Wrapper>
      );
    } else {
      return null;
    }
  } else if (type === 'autogroup_multi_user_recommended') {
    const { relevantUsers, autoGroupTitle, action, tooltipContents } = props;

    return (
      <Wrapper variant={variant}>
        <AuthorHeader
          kind="multi-user"
          users={relevantUsers}
          userTitle="published"
          groupName={autoGroupTitle}
          action={action}
          paperType="paper"
          showAvatar
          size="md"
          avatarInteractionContents={tooltipContents}
          impressionPropsGen={impressionPropsGen}
          variant={variant}
        />
      </Wrapper>
    );
  } else if (type === 'recommended') {
    return variant === 'default' ? (
      <Wrapper variant={variant}>
        <AuthorHeader
          kind="recommendation"
          recommendation={{
            name: 'Academia Paper Recommendations',
            subtitle: 'Recommended based on your interests',
          }}
          size="md"
          showAvatar={true}
          impressionPropsGen={impressionPropsGen}
          variant={variant}
        />
      </Wrapper>
    ) : null;
  } else {
    return null;
  }
};
